import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { LearnerRecord, LearnerRecordAssessment, LearnerRecordIdentities } from "@data/learnerRecord";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { ServiceResponse } from "../data";
import { ResponseHandler } from "@service/system/responseHandler.service";
import { VerificationCodeResponse, VerifyAccountResponse } from "../data/learnerRecord";
const API_URL = API.API_URL;

/**
 * A Learner Record Management service that routes service calls.
 *
 * @author    John Ying <john.ying@childrenshospitals.org>
 * @created   12/28/2022
 */
@Injectable()
export class LearnerRecordManagementService {
	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	/**
	 * Gets Learner Records using the given search parameters.
	 *
	 * @param learnerId         - the learner's id
	 * @param firstName         - the first name
	 * @param lastName          - the last name
	 * @param email             - the email
	 * @param organizationId    - the organization id
	 * @param take				- the number of records to return (default to 50)
	 * @param skip				- the number of records to paginate from, e.g. 1-25, 26-51, etc.,
	 * 								defaults to zero to start at the first record of the entire list
	 *
	 * @returns the list of Learner Records response
	 */
	public getLearnerRecords(
		learnerId: number,
		firstName?: string,
		lastName?: string,
		email?: string,
		organizationId?: number,
		take = 50,
		skip = 0
	): Observable<ServiceResponse<LearnerRecord[]>> {
		const params = {
			firstName: firstName ? firstName : "",
			lastName: lastName ? lastName : "",
			email: email ? email : "",
			organizationId: organizationId ? organizationId : 0,
			learnerId: learnerId ? learnerId : 0,
			take: take,
			skip: skip
		};
		return this.responseHandler.httpGet<LearnerRecord[]>(`${API_URL}/learner/find`, params);
	}

	/**
	 * Gets Learner Records for a given PLS Learner_ID.
	 *
	 * @param learnerId         - the learner's id
	 *
	 * @returns the list of Learner Records response
	 */
	public getLearnerRecordByLearnerId(learnerId: number): Observable<ServiceResponse<LearnerRecord[]>> {
		return this.responseHandler.httpGet<LearnerRecord[]>(`${API_URL}/learner/${learnerId}`);
	}

	/**
	 * Gets Exportable Text document of Learner's CE Certificate records
	 *
	 * @param learnerId         - the learner's id
	 *
	 * @returns plaintext document containing learner certificate history
	 */
	public getLearnerCertificateTranscriptExport(learnerId: number): any {
		return this.httpManager.httpPostBlob<any>(`${API_URL}/export/learner/${learnerId}/certificate-transcript`, null);
	}

	/**
	 * Gets up to the x most recent Learner Records Transactions.
	 *
	 * @param learnerId    - the learner's id
	 * @param type    - the transaction type (current values "cert", "assess")
	 * @param length - number of records to return
	 *
	 * @returns the list of recent Learner Transactions response
	 */
	public getRecordTransactions(
		learnerId: number,
		type: string,
		length: number
	): Observable<ServiceResponse<LearnerRecordAssessment[]>> {
		return this.responseHandler.httpGet<LearnerRecordAssessment[]>(
			`${API_URL}/learner/${learnerId}/transactions/${type}/${length}`
		);
	}

	/**
	 * Gets up to the x most recent Learner Records Certificates.
	 *
	 * @param learnerId    - the learner's id
	 * @param email - number of records to return
	 *
	 * @returns the list of recent Learner Certificates response
	 */
	public getLearnerIdentities(
		learnerId: number,
		email: string
	): Observable<ServiceResponse<LearnerRecordIdentities[]>> {
		const params = {
			email: email ? email : ""
		};
		return this.responseHandler.httpGet<LearnerRecordIdentities[]>(
			`${API_URL}/learner/${learnerId}/identities`,
			params
		);
	}

	/**
	 * Merge Records for a given Learner_ID.
	 *
	 * @param learnerId    - the learner's id
	 * @param identities - number of records to return
	 *
	 * @returns the list of recent Learner Certificates response
	 */
	public mergeLearnerIdentities(learnerId: number, identities: string): Observable<any> {
		const params = {
			identities: identities
		};
		return this.httpManager.httpGet<any>(`${API_URL}/learner/${learnerId}/merge-identities`, params);
	}

	/**
	 * Request user email verification code be sent to a given email address.
	 * @param learnerEmail
	 * @returns service response containing Verification Code sent to learner's email address
	 */
	public requestLearnerVerificationCodeByEmail(
		learnerEmail: string
	): Observable<ServiceResponse<VerificationCodeResponse[]>> {
		return this.responseHandler.httpGet<VerificationCodeResponse[]>(
			`${API_URL}/user/external/${learnerEmail}/requestcode`
		);
	}

	/**
	 * Verifies User Email with Verification Code
	 * @param body
	 * @returns Verification request status and message
	 */
	public verifyUserEmail(
		learnerId: number,
		body: {
			Email: string;
			VerificationCode: number;
		},
		userID: unknown
	): Observable<ServiceResponse<VerifyAccountResponse[]>> {
		return this.responseHandler.httpPost<VerifyAccountResponse[]>(
			`${API_URL}/learner/${learnerId}/verifyemail`,
			body,
			{ modifiedBy: userID }
		);
	}

	/**
	 * Updates Learner Record
	 * @param learnerId
	 * @param body
	 * @returns Update request status and message
	 */
	public setLearnerRecord(
		learnerId: number,
		body: LearnerRecord,
		userID: unknown
	): Observable<ServiceResponse<VerificationCodeResponse[]>> {
		return this.responseHandler.httpPost<VerificationCodeResponse[]>(`${API_URL}/learner/${learnerId}/set`, body, {
			modifiedBy: userID
		});
	}
}
